import Form, { FormInstance } from 'antd/lib/form';
import _ from 'lodash';
import { useState } from 'react';
import { PjmRegistrationStatus } from 'src/domain/pjm/dto/enums';
import { PjmRegistrationDto } from 'src/domain/pjm/dto/PjmRegistrationDto';
import { PjmRegistrationModal } from 'src/pages/pjm/PjmRegistrations/components/PjmRegistrationModal/PjmRegistrationModal';
import { PjmLocationForm } from './PjmLocationTransformer';
import Tooltip from 'antd/lib/tooltip';
import Button from 'antd/lib/button';

interface IRegisterButtonProps {
    form: FormInstance<PjmLocationForm>;
    location: PjmLocationForm;
    onClose: (actionTaken: boolean) => void;
}

export function RegisterButton({ form, location, onClose }: IRegisterButtonProps) {
    const [showRegisterModal, setShowRegisterModal] = useState(false);

    // Force re-render when any form field changes
    Form.useWatch([], form);

    /** initialize new registration with default values */
    const registration: PjmRegistrationDto = {
        locationUuid: location.uuid,
        id: undefined,
        // @ts-expect-error
        uuid: undefined,
        summaryInfo: {
            name: location.summaryInfo.name,
            program: undefined,
            CSP: { ...location.summaryInfo.CSP },
            EDC: { ...location.summaryInfo.EDC },
            zone: location.summaryInfo.zone,
            pricingPoint: location.summaryInfo.pricingPoint,
            status: PjmRegistrationStatus.NEW,
        },
        /** copy from location */
        locationSummaryInfo: {
            energyLossFactor: location.profileInfo?.energyLossFactor || 0,
            capacityLossFactor: location.profileInfo?.capacityLossFactor || 0,
            peakLoadContribution: location.profileInfo?.peakLoadContribution || 0,
            winterPeakLoad: location.profileInfo?.winterPeakLoad || 0,
            maximumLoadReductionCapability: location.profileInfo?.loadReduction || 0,
        },
        /** copy from location */
        capacityInfo: {
            peakLoadContribution: location.profileInfo?.peakLoadContribution,
            capacityLossFactor: location.profileInfo?.capacityLossFactor,
        },
        location: _.cloneDeep(location),
    };

    /** we can register a location only if it was saved and has not been registered yet */
    const _isFormSaved = isFormSaved(form);
    const isNew = !location?.uuid;
    const meterInfo = form.getFieldValue(['meterInfo']);
    const hasMeterEquipment = !!meterInfo?.length;
    const canRegister = !isNew && hasMeterEquipment && _isFormSaved;

    async function onRegister() {
        setShowRegisterModal(true);
    }

    function onRegisterModalClose(actionTaken: boolean) {
        setShowRegisterModal(false);
        if (actionTaken) {
            onClose(true);
        }
    }

    let registerTooltip = 'Click to register this location';
    if (isNew || !_isFormSaved) {
        registerTooltip = 'Please save the location first before you can register it';
    } else if (!hasMeterEquipment) {
        registerTooltip = 'Please add meter equipment and save location before you can register';
    }

    return (
        <>
            <Tooltip title={registerTooltip}>
                <Button key="pjm-register-location-button" type="primary" disabled={!canRegister} onClick={onRegister}>
                    Register
                </Button>
            </Tooltip>
            {showRegisterModal && <PjmRegistrationModal key="pjm-registration-modal" onClose={onRegisterModalClose} value={registration} />}
        </>
    );
}

function isFormSaved(form: FormInstance<PjmLocationForm>) {
    /**
     * TODO: ideally we should compare form initial values with actual values here
     */
    const isFieldsTouched = form.isFieldsTouched();
    return !isFieldsTouched;
}
